import { Point } from 'leaflet';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ISegmentation } from '../annotations.interface';
import { IHiddenSegmentation } from '../annotations.store';
import { IAnnotationTypeOptions } from '../submodules/annotationTypes/models/annotationTypeOptions';
import { SegmentationContextMenuContainer } from '../submodules/segmentations/segmentationContextMenu/containers/SegmentationContextMenu.container';
import { AttributePopupControl } from './AttributePopupControl';
import { MapContextMenuSuppresor } from './MapContextMenuSuppresor';
import { SegmentationFocusControl } from './SegmentationFocusControl';
import { SegmentationFrameDrawControl } from './SegmentationFrameDrawControl';
import { SegmentationLayerFeatures } from './SegmentationLayerFeatures';

interface IProps {
  segmentations: ISegmentation[];
  annotationTypesOptions: IAnnotationTypeOptions[];
  hiddenSegmentations: IHiddenSegmentation[];
  selectedSegmentation?: ISegmentation;
  hoveredSegmentation?: string;
  invalidSegmentation?: ISegmentation;
  readonly?: boolean;
  editable: boolean;
  freeDrawInProgress: boolean;
  selectedAttributes: string[];
  showAttributesHeaders: boolean;
  featureInEditId?: string;
  opacityLevel: number;
  zoomLevel: number;
  activeAnnotationTypeId?: string;
  drawFocusFrameForId?: string;
  reactLeafletRenderFingerprint: string;
  imageId: string;
  onSegmentationSelected: (id: string) => void;
  onSegmentationDeselected: () => Promise<void>;
  onNextSegmentationSelected: () => ISegmentation | undefined;
  onPreviousSegmentationSelected: () => ISegmentation | undefined;
  onSegmentationTabed: (id: string) => void;
  onSegmentationOver: (segmentation: string) => void;
  onSegmentationOut: () => void;
}

export const SegmentationsLayer = observer((props: IProps): ReactElement => {
  const invalidQuestionsAnnotationTypesId = new Set(
    props.segmentations.filter((s) => s.questions.some((q) => q.status.isValid === false)).map((s) => s.feature.properties?.annotationTypeId as string)
  );

  const hiddenAnnotationTypes = props.annotationTypesOptions.filter((x) => !x.isVisible && !invalidQuestionsAnnotationTypesId.has(x.id)).map((x) => x.id);

  const visibleSegmentations = props.segmentations
    .filter(
      ({ feature }) =>
        !props.hiddenSegmentations.some((x) => x.featureId === feature.id) && !hiddenAnnotationTypes.some((x) => x === feature.properties?.annotationTypeId)
    )
    .map((x) => toJS(x));

  return (
    <SegmentationFrameDrawControl
      readonly={props.readonly ?? false}
      editable={props.editable}
      segmentation={
        props.freeDrawInProgress || !props.drawFocusFrameForId ? undefined : props.segmentations.find((x) => x.feature.id === props.drawFocusFrameForId)
      }
      onSegmentationSelected={props.readonly ? props.onSegmentationSelected : props.onSegmentationTabed}
      onSegmentationDeselected={props.onSegmentationDeselected}
      onNextSegmentationSelected={props.freeDrawInProgress ? (): undefined => undefined : props.onNextSegmentationSelected}
      onPreviousSegmentationSelected={props.freeDrawInProgress ? (): undefined => undefined : props.onPreviousSegmentationSelected}
    >
      <AttributePopupControl
        selectedAttributes={props.selectedAttributes}
        selectedSegmentation={props.selectedSegmentation}
        hoveredSegmentation={props.hoveredSegmentation}
        showAttributesHeaders={props.showAttributesHeaders}
        segmentations={visibleSegmentations}
        imageId={props.imageId}
      >
        <MapContextMenuSuppresor>
          <SegmentationFocusControl invalidSegmentation={props.invalidSegmentation}>
            <SegmentationContextMenuContainer>
              {(handleMenuOpen: (position: Point, segmentationId: string) => void, contextMenuSegmentationId: string | undefined): ReactElement => (
                <SegmentationLayerFeatures
                  segmentations={visibleSegmentations}
                  selectedSegmentation={props.selectedSegmentation}
                  hoveredSegmentation={props.hoveredSegmentation}
                  contextMenuSegmentationId={contextMenuSegmentationId}
                  readonly={props.readonly}
                  featureInEditId={props.featureInEditId}
                  opacityLevel={props.opacityLevel}
                  zoomLevel={props.zoomLevel}
                  activeAnnotationTypeId={props.activeAnnotationTypeId}
                  onSegmentationSelected={props.onSegmentationSelected}
                  onSegmentationOver={props.onSegmentationOver}
                  onSegmentationOut={props.onSegmentationOut}
                  onContextMenu={handleMenuOpen}
                  reactLeafletRenderFingerprint={props.reactLeafletRenderFingerprint}
                />
              )}
            </SegmentationContextMenuContainer>
          </SegmentationFocusControl>
        </MapContextMenuSuppresor>
      </AttributePopupControl>
    </SegmentationFrameDrawControl>
  );
});
